import type {
    NewOutlookSpace,
    NewOutlookSpaceAsset,
    OutlookSpace,
    OutlookSpaceAsset,
    OutlookSpaceAssetId,
    OutlookSpaceAssetSyncResult,
    OutlookSpaceId,
    OutlookSpacesFilter,
    PatchOutlookSpace,
    PatchOutlookSpaceAsset,
    SyncParameters,
} from 'owa-timestream-types';

// For documentation of this API, see
// https://outlook-sdf.office.com/ows/swagger/index.html#/OutlookSpaces

type LocalAssets = { [key: string]: OutlookSpaceAsset };
type LocalOutlookSpace = Omit<OutlookSpace, 'assets'> & { assets: LocalAssets };
type LocalOutlookSpaces = { [key: string]: LocalOutlookSpace };

function getLocalOutlookSpaces() {
    return JSON.parse(localStorage.LocalOutlookSpaces || '{}') as LocalOutlookSpaces;
}

function setLocalOutlookSpaces(spaces: LocalOutlookSpaces) {
    localStorage.LocalOutlookSpaces = JSON.stringify(spaces);
}

function clone<T>(o: T): T {
    return JSON.parse(JSON.stringify(o)) as T;
}

function asOutlookSpace(localSpace: LocalOutlookSpace): OutlookSpace {
    const { assets, ...localSpaceInfo } = localSpace;
    const outlookSpace = { ...localSpaceInfo };
    return outlookSpace as OutlookSpace;
}

//
// Outlook Spaces (Collection)
//
export function getOutlookSpaces(spacesFilter?: OutlookSpacesFilter) {
    return new Promise<OutlookSpace[]>(resolve => {
        const spaces = getLocalOutlookSpaces();
        let listOfSpaces = Object.keys(spaces)
            .map(id => asOutlookSpace(spaces[id]))
            .sort((a, b) => (a.createdDateTime < b.createdDateTime ? -1 : 1));

        const appNameFilter = spacesFilter?.appName || '';
        if (appNameFilter) {
            listOfSpaces = listOfSpaces.filter(space => {
                return space.appName && space.appName.indexOf(appNameFilter) > -1;
            });
        }

        resolve(listOfSpaces);
    });
}

export function createOutlookSpace(newOutlookSpace: NewOutlookSpace) {
    return new Promise<OutlookSpace>(resolve => {
        const { assets, ...newSpaceInfo } = newOutlookSpace;
        const o = clone(newSpaceInfo) as LocalOutlookSpace;
        const d = new Date();
        o.id = `VOLATILE_${d.getTime()}` as OutlookSpaceId;
        o.createdDateTime = d.toISOString();
        o.lastModifiedDateTime = d.toISOString();
        o.assets =
            assets?.reduce((acc, asset, idx) => {
                const o = clone(asset) as OutlookSpaceAsset;
                const d = new Date();
                o.id = `VOLATILE_${d.getTime()}_${idx}` as OutlookSpaceAssetId;
                o.createdDateTime = d.toISOString();
                o.lastModifiedDateTime = d.toISOString();
                acc[o.id] = o;
                return acc;
            }, {}) || {};
        const spaces = getLocalOutlookSpaces();
        spaces[o.id] = o;
        setLocalOutlookSpaces(spaces);
        resolve(asOutlookSpace(o));
    });
}

//
// Outlook Space (Entity)
//
export function getOutlookSpace(outlookSpaceId: OutlookSpaceId) {
    return new Promise<OutlookSpace>((resolve, reject) => {
        const space = getLocalOutlookSpaces()[outlookSpaceId];
        if (!space) {
            reject('VOLATILE PROJECT NOT FOUND');
        } else {
            resolve(asOutlookSpace(space));
        }
    });
}

export function deleteOutlookSpace(outlookSpaceId: OutlookSpaceId) {
    return new Promise<void>((resolve, reject) => {
        const spaces = getLocalOutlookSpaces();
        if (!spaces[outlookSpaceId]) {
            reject('VOLATILE PROJECT NOT FOUND');
        } else {
            delete spaces[outlookSpaceId];
            setLocalOutlookSpaces(spaces);
            resolve();
        }
    });
}

export function patchOutlookSpace(outlookSpaceId: OutlookSpaceId, changes: PatchOutlookSpace) {
    return new Promise<OutlookSpace>((resolve, reject) => {
        const spaces = getLocalOutlookSpaces();
        if (!spaces[outlookSpaceId]) {
            reject('VOLATILE PROJECT NOT FOUND');
        } else {
            const updatedSpace = (spaces[outlookSpaceId] = {
                ...spaces[outlookSpaceId],
                ...clone(changes),
                lastModifiedDateTime: new Date().toISOString(),
            });
            setLocalOutlookSpaces(spaces);
            resolve(asOutlookSpace(updatedSpace));
        }
    });
}

//
// Outlook Space Assets (Collection)
//
export async function getOutlookSpaceAssets(outlookSpaceId: OutlookSpaceId) {
    return new Promise<OutlookSpaceAsset[]>((resolve, reject) => {
        const spaces = getLocalOutlookSpaces();
        const assets = spaces[outlookSpaceId]?.assets;
        if (!assets) {
            reject('VOLATILE PROJECT NOT FOUND');
        } else {
            const listOfAssets = Object.keys(assets)
                .map(id => assets[id] as OutlookSpaceAsset)
                .sort((a, b) => (a.createdDateTime < b.createdDateTime ? -1 : 1));
            for (const asset of listOfAssets) {
                asset.x = asset.x || 0;
                asset.y = asset.y || 0;
            }
            resolve(listOfAssets);
        }
    });
}

export function canSyncAssets() {
    return false;
}

export function syncAssets(
    outlookSpaceId: OutlookSpaceId,
    syncParameters: SyncParameters
): Promise<OutlookSpaceAssetSyncResult> {
    return Promise.reject();
}

export async function createOutlookSpaceAsset(
    outlookSpaceId: OutlookSpaceId,
    newAsset: NewOutlookSpaceAsset
) {
    return new Promise<OutlookSpaceAsset>((resolve, reject) => {
        const spaces = getLocalOutlookSpaces();
        const assets = spaces[outlookSpaceId]?.assets;
        if (!assets) {
            reject('VOLATILE PROJECT NOT FOUND');
        } else {
            const o = clone(newAsset) as OutlookSpaceAsset;
            const d = new Date();
            o.id = `VOLATILE_${d.getTime()}` as OutlookSpaceAssetId;
            o.createdDateTime = d.toISOString();
            o.lastModifiedDateTime = d.toISOString();
            assets[o.id] = o;
            setLocalOutlookSpaces(spaces);
            resolve(o);
        }
    });
}

//
// Outlook Space Asset (Entity)
//
export function deleteOutlookSpaceAsset(
    outlookSpaceId: OutlookSpaceId,
    assetId: OutlookSpaceAssetId
) {
    return new Promise<void>((resolve, reject) => {
        const spaces = getLocalOutlookSpaces();
        const assets = spaces[outlookSpaceId]?.assets;
        if (!assets) {
            reject('VOLATILE PROJECT NOT FOUND');
        } else {
            const asset = assets[assetId];
            if (!asset) {
                reject('VOLATILE ASSET NOT FOUND');
            }
            delete assets[assetId];

            Object.keys(assets).forEach(key => {
                if (assets[key].parentId == assetId) {
                    delete assets[key];
                }
            });

            setLocalOutlookSpaces(spaces);

            // It looks like buckets has some trouble when we delete assets and we complete the call synchronously.
            // There's probably a couple of satchel mutators running in the wrong order and it causes the bucket to
            // try to render the item that got deleted and fail. Pretending we are async brings us back to the usual state.
            setTimeout(resolve, 1);
        }
    });
}

export function patchOutlookSpaceAsset(
    outlookSpaceId: OutlookSpaceId,
    assetId: OutlookSpaceAssetId,
    changes: PatchOutlookSpaceAsset,
    returnFullObject?: boolean
) {
    return new Promise<OutlookSpaceAsset | void | { lastModifiedDateTime: string }>(
        (resolve, reject) => {
            const spaces = getLocalOutlookSpaces();
            const assets = spaces[outlookSpaceId]?.assets;
            if (!assets) {
                reject('VOLATILE PROJECT NOT FOUND');
            } else {
                const asset = assets[assetId];
                if (!asset) {
                    reject('VOLATILE ASSET NOT FOUND');
                }

                const updatedAsset: OutlookSpaceAsset = (assets[assetId] = {
                    ...asset,
                    ...clone(changes),
                    parentId:
                        changes.parentId === null ? undefined : changes.parentId || asset.parentId,
                    lastModifiedDateTime: new Date().toISOString(),
                });

                setLocalOutlookSpaces(spaces);
                resolve(updatedAsset);
            }
        }
    );
}

//
// Asset Reorder Children
//
export function reorderOutlookSpaceAssetChildren(
    outlookSpaceId: OutlookSpaceId,
    assetId: OutlookSpaceAssetId
) {
    return Promise.reject<OutlookSpaceAsset[]>('NOT IMPLEMENTED');
}

//
// Recycle Bin
//
export function emptyOutlookSpaceRecycleBin(outlookSpaceId: OutlookSpaceId) {
    return Promise.resolve();
}
